import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import { ErrorState, ToastProvider } from "@ady/ui";
import { queryClient } from "./api/queries";
import { StepUpProvider } from "./auth/StepUp";
import { takeFonepayReturn } from "./billing/payReturn";
import { router } from "./router";
import { applyTheme, readTheme } from "./theme";
import "./styles.css";

applyTheme(readTheme());

// After a deploy the new service worker takes over open pages at once (skipWaiting + clientsClaim);
// the page then moves onto the new build so it never keeps running code that predates the deploy.
// Owner, 2026-11-01: never reload under the user's hands (a reload a few seconds after opening a page
// swallowed clicks, e.g. "Use this theme", and would drop a half-typed form). The move happens at the
// next page change (a full load of that page), at once when the tab is in the background, or when the
// old build fails to load one of its files. Only when a worker already controlled the page: the very
// first install doesn't reload anyone.
if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
  let updated = false;
  let moved = false;
  const move = (href?: string) => {
    if (moved) return;
    moved = true;
    if (href) window.location.assign(href);
    else window.location.reload();
  };
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    updated = true;
    if (document.visibilityState === "hidden") move();
  });
  document.addEventListener("visibilitychange", () => {
    if (updated && document.visibilityState === "hidden") move();
  });
  router.subscribe("onBeforeNavigate", (e) => {
    if (updated && e.pathChanged) move(e.toLocation.href);
  });
  // A lazy page of the old build that the deploy removed: load the new build instead of failing.
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    move();
  });
}

// An open admin tab looks for a new build when it comes back into view and every 15 minutes, not only
// when a page is first opened (a tab left open all day otherwise never saw a deploy).
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.ready.then((reg) => {
    const check = () => void reg.update().catch(() => undefined);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
    setInterval(check, 15 * 60 * 1000);
  });
}

/**
 * DEV ONLY: the MSW mock API. `import.meta.env.DEV` is replaced with `false` in production builds,
 * so this branch and its dynamic import are removed; scripts/assert-no-mocks.mjs double-checks.
 * Opt out in dev with VITE_ADY_MOCKS=off (to talk to a local `wrangler dev` API instead).
 */
async function enableMocks(): Promise<void> {
  if (import.meta.env.DEV && import.meta.env.VITE_ADY_MOCKS !== "off") {
    const { startMocks } = await import("./mocks/browser");
    await startMocks();
    (globalThis as { __ADY_MOCKS__?: boolean }).__ADY_MOCKS__ = true;
    // D-065: the console's "Open read-only view" link (the server's /api/v1/support/enter).
    if (location.pathname === "/support-view") {
      const { enterSupportView } = await import("./mocks/modules/platform");
      await enterSupportView(new URLSearchParams(location.search));
    }
  }
}

function render(mocksFailed: boolean) {
  // D-122: back from Fonepay's payment page → the page the shop paid from (the shell shows the result).
  const back = takeFonepayReturn();
  if (back) router.history.replace(back);
  const root = document.getElementById("root");
  if (!root) throw new Error("#root missing");
  createRoot(root).render(
    <StrictMode>
      {mocksFailed ? (
        // Dev only: never a blank page when the MSW worker cannot register.
        <ErrorState
          size="page"
          title={t("dev.mocksFailedTitle")}
          body={t("dev.mocksFailedBody")}
          onRetry={() => location.reload()}
        />
      ) : (
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <StepUpProvider>
              <RouterProvider router={router} />
            </StepUpProvider>
          </ToastProvider>
        </QueryClientProvider>
      )}
    </StrictMode>,
  );
}

enableMocks().then(
  () => render(false),
  (err: unknown) => {
    console.error("mock API failed to start", err);
    render(true);
  },
);
